import React, { useEffect, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Post } from '../types';
import { cn } from '../../../utils/cn';

type UiComment = {
  id: string;
  authorId?: string;
  authorName: string;
  authorRole: Post['authorRole'];
  authorAvatar?: string;
  content: string;
  timestamp: Date;
  parentCommentId?: string | null;
  likes?: number;
  isLiked?: boolean;
  adores?: number;
  isAdored?: boolean;
};

type TreeComment = UiComment & { replies: TreeComment[] };

const AutoPlayVideo: React.FC<{ src: string; className?: string }> = ({ src, className }) => {
  const ref = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    el.muted = true;
    (el as any).playsInline = true;

    if (!('IntersectionObserver' in window)) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          const p = el.play();
          if (p && typeof (p as any).catch === 'function') (p as any).catch(() => {});
        } else {
          try {
            el.pause();
          } catch {
            // ignore
          }
        }
      },
      { threshold: [0, 0.6, 1] }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [src]);

  return (
    <video
      ref={ref}
      src={src}
      controls
      muted
      playsInline
      preload="metadata"
      className={cn('w-full max-h-[420px] bg-black', className)}
    />
  );
};

interface PostCardProps {
  post: Post;
  currentUserId?: string;
  canDeletePost?: boolean;
  onDeletePost?: (postId: string) => void;
  onThumbLike: (postId: string) => void;
  onAdore: (postId: string) => void;
  onViewPostReactions?: (postId: string, reaction: 'thumb' | 'adore') => void;
  onComment: (postId: string, content: string) => void;
  onShare: (postId: string) => void;
  onViewComments: (postId: string) => void;
  onDestinationClick?: (post: Post) => void;
  onApprove?: (postId: string, visibility?: Post['visibility']) => void;
  onReject?: (postId: string) => void;
  onToggleEventRegistration?: (postId: string, nextRegister: boolean) => void;
  comments?: UiComment[];
  commentsLoading?: boolean;
  commentsHasMore?: boolean;
  onLoadCommentsPreview?: (postId: string) => void;
  onReplyComment?: (postId: string, parentCommentId: string, content: string) => void;
  onThumbLikeComment?: (postId: string, commentId: string) => void;
  onAdoreComment?: (postId: string, commentId: string) => void;
  onViewCommentReactions?: (postId: string, commentId: string, reaction: 'thumb' | 'adore') => void;
  onDeleteComment?: (postId: string, commentId: string) => void;
}

const PostCard: React.FC<PostCardProps> = ({
  post,
  currentUserId,
  canDeletePost,
  onDeletePost,
  onThumbLike,
  onAdore,
  onViewPostReactions,
  onComment,
  onShare,
  onViewComments,
  onDestinationClick,
  onApprove,
  onReject,
  onToggleEventRegistration,
  comments = [],
  commentsLoading,
  commentsHasMore,
  onLoadCommentsPreview,
  onReplyComment,
  onThumbLikeComment,
  onAdoreComment,
  onViewCommentReactions,
  onDeleteComment,
}) => {
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalIndex, setImageModalIndex] = useState(0);
  const [moderationVisibility, setModerationVisibility] = useState<Post['visibility']>(post.visibility);
  const didAutoLoadComments = useRef(false);

  useEffect(() => {
    setModerationVisibility(post.visibility);
  }, [post.id, post.visibility]);

  useEffect(() => {
    if (didAutoLoadComments.current) return;
    if (!post.comments) return;
    if (post.comments < 1) return;
    if (comments.length > 0) {
      didAutoLoadComments.current = true;
      return;
    }
    if (commentsLoading) return;
    didAutoLoadComments.current = true;
    onLoadCommentsPreview?.(post.id);
  }, [comments.length, commentsLoading, onLoadCommentsPreview, post.comments, post.id]);

  const openImageModal = (index: number) => {
    const imgs = post.images || [];
    if (!imgs.length) return;
    const safeIndex = Math.max(0, Math.min(index, imgs.length - 1));
    setImageModalIndex(safeIndex);
    setImageModalOpen(true);
  };

  const closeImageModal = () => setImageModalOpen(false);

  const goPrevImage = () => {
    const imgs = post.images || [];
    if (!imgs.length) return;
    setImageModalIndex((prev) => (prev - 1 + imgs.length) % imgs.length);
  };

  const goNextImage = () => {
    const imgs = post.images || [];
    if (!imgs.length) return;
    setImageModalIndex((prev) => (prev + 1) % imgs.length);
  };

  useEffect(() => {
    if (!imageModalOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeImageModal();
      if (e.key === 'ArrowLeft') goPrevImage();
      if (e.key === 'ArrowRight') goNextImage();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [imageModalOpen, post.images]);

  const handleSubmitComment = () => {
    if (commentText.trim()) {
      onComment(post.id, commentText);
      setCommentText('');
    }
  };

  const handleSubmitReply = (parentId: string) => {
    if (!replyText.trim()) return;
    onReplyComment?.(post.id, parentId, replyText);
    setReplyText('');
    setReplyToId(null);
  };

  const getCategoryColor = (category: Post['category']) => {
    const colors = {
      announcement: 'bg-primary/10 text-primary',
      achievement: 'bg-success/10 text-success',
      event: 'bg-accent/10 text-accent',
      discussion: 'bg-secondary/10 text-secondary',
      resource: 'bg-warning/10 text-warning',
    };
    return colors[category] || colors.discussion;
  };

  const getRoleColor = (role: string) => {
    const colors = {
      admin: 'text-error',
      teacher: 'text-primary',
      student: 'text-accent',
      parent: 'text-secondary',
      secretariat: 'text-primary',
    };
    return colors[role as keyof typeof colors] || 'text-foreground';
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'administrator';
      case 'secretariat':
        return 'secretariat';
      case 'teacher':
        return 'profesor';
      case 'student':
        return 'elev';
      case 'parent':
        return 'părinte';
      default:
        return role;
    }
  };

  const getCategoryLabel = (category: Post['category']) => {
    switch (category) {
      case 'announcement':
        return 'anunț';
      case 'achievement':
        return 'realizare';
      case 'event':
        return 'eveniment';
      case 'discussion':
        return 'discuție';
      case 'resource':
        return 'resursă';
      default:
        return category;
    }
  };

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Chiar acum';
    if (diffInMinutes < 60) return `${diffInMinutes} min în urmă`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} h în urmă`;
    return `${Math.floor(diffInMinutes / 1440)} zile în urmă`;
  };

  const contentPreview = post.content.length > 300 ? post.content.substring(0, 300) + '...' : post.content;

  const visibilityLabel = (v: Post['visibility']) => {
    if (v === 'public') return 'Publică';
    if (v === 'class') return 'Clasă';
    return 'Școală';
  };

  const isAwardImageUrl = (url: string | undefined | null) => {
    const u = String(url || '');
    if (!u) return false;
    return (
      u.includes('/uploads/system/award_student.png') ||
      u.includes('/uploads/system/award_class.png') ||
      u.includes('/uploads/system/awards/schools/')
    );
  };

  const destinationText = () => {
    if (post.visibility === 'public') return post.schoolName ? `Publică • ${post.schoolName}` : 'Publică';
    if (post.visibility === 'class') return `Clasă: ${post.classroomName || '—'}`;
    return `Școală: ${post.schoolName || '—'}`;
  };

  const buildCommentTree = (list: UiComment[]): TreeComment[] => {
    const byId = new Map<string, TreeComment>();
    for (const c of list) {
      byId.set(c.id, { ...c, replies: [] });
    }
    const roots: TreeComment[] = [];
    for (const c of byId.values()) {
      const parentId = c.parentCommentId || null;
      if (parentId && byId.has(parentId)) {
        byId.get(parentId)!.replies.push(c);
      } else {
        roots.push(c);
      }
    }
    const sortDesc = (a: UiComment, b: UiComment) => b.timestamp.getTime() - a.timestamp.getTime();
    const sortAsc = (a: UiComment, b: UiComment) => a.timestamp.getTime() - b.timestamp.getTime();
    roots.sort(sortDesc);
    const sortReplies = (node: TreeComment) => {
      node.replies.sort(sortAsc);
      for (const ch of node.replies) sortReplies(ch);
    };
    for (const r of roots) sortReplies(r);
    return roots;
  };

  const renderComment = (c: TreeComment, depth: number) => {
    const liked = Boolean(c.isLiked);
    const adored = Boolean(c.isAdored);
    const likesCount = Number(c.likes || 0);
    const adoresCount = Number(c.adores || 0);
    const canDelete =
      Boolean(onDeleteComment) &&
      Boolean(currentUserId) &&
      (String(currentUserId) === String(post.authorId) ||
        (c.authorId != null && String(currentUserId) === String(c.authorId)));
    return (
      <div key={c.id} className={depth ? 'ml-8 border-l border-border pl-3' : ''}>
        <div className="flex items-start gap-2">
          {c.authorAvatar ? (
            <Image
              src={c.authorAvatar}
              alt={`Poză de profil: ${c.authorName || 'Utilizator'}`}
              className="w-8 h-8 rounded-full object-cover border border-border flex-shrink-0 bg-background"
            />
          ) : (
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-background border border-border text-foreground text-xs font-semibold flex-shrink-0">
              {(c.authorName || 'U').charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="w-full rounded-2xl bg-muted px-3 py-2">
              <div className="flex items-center">
                <span className="text-sm font-semibold text-foreground truncate max-w-full">{c.authorName}</span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                <span>{getRoleLabel(c.authorRole)}</span>
                <span>•</span>
                <span>{formatTimestamp(c.timestamp)}</span>
              </div>
              <div className="mt-0.5 text-sm text-foreground break-words leading-snug">{c.content}</div>
            </div>
            <div className="flex items-center gap-3 mt-1.5 pl-1">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onThumbLikeComment?.(post.id, c.id);
                }}
                className={`text-xs flex items-center gap-1.5 ${
                  liked ? 'text-error' : 'text-muted-foreground hover:text-error'
                }`}
                disabled={!onThumbLikeComment}
              >
                <Icon name="ThumbsUp" size={14} />
              </button>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewCommentReactions?.(post.id, c.id, 'thumb');
                }}
                disabled={!onViewCommentReactions || likesCount < 1}
                aria-label="Vezi aprecierile"
              >
                {likesCount}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAdoreComment?.(post.id, c.id);
                }}
                className={`text-xs flex items-center gap-1.5 ${
                  adored ? 'text-error' : 'text-muted-foreground hover:text-error'
                }`}
                disabled={!onAdoreComment}
              >
                <Icon name="Heart" size={14} />
              </button>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-primary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewCommentReactions?.(post.id, c.id, 'adore');
                }}
                disabled={!onViewCommentReactions || adoresCount < 1}
                aria-label="Vezi adorurile"
	              >
	                {adoresCount}
	              </button>
	              {canDelete ? (
	                <button
	                  type="button"
	                  onClick={(e) => {
	                    e.preventDefault();
	                    e.stopPropagation();
	                    onDeleteComment?.(post.id, c.id);
	                  }}
	                  className="text-xs text-muted-foreground hover:text-error"
	                  aria-label="Sterge comentariul"
	                >
	                  <Icon name="Trash2" size={14} />
	                </button>
	              ) : null}
	              <button type="button"
	                onClick={() => {
	                  setReplyToId(replyToId === c.id ? null : c.id);
	                  setReplyText('');
                }}
                className="text-xs text-muted-foreground hover:text-primary"
                disabled={!onReplyComment}
              >
                Răspunde
              </button>
            </div>
            {replyToId === c.id ? (
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Scrie un răspuns..."
                  className="flex-1 px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  onKeyPress={(e) => e.key === 'Enter' && handleSubmitReply(c.id)}
                />
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleSubmitReply(c.id)}
                  disabled={!replyText.trim()}
                  iconName="Send"
                >
                  Publică
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        {c.replies.length > 0 ? (
          <div className="mt-2 space-y-2">
            {c.replies.map((r) => renderComment(r, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div id={`post-${post.id}`} className="bg-card border border-border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
      {post.status === 'pending' && (
        <div className="flex items-center justify-between gap-2 px-4 py-2 bg-warning/10 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <Icon name="Clock" size={16} className="text-warning" />
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <span className="text-xs font-medium text-warning">În așteptare aprobarea</span>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground">Destinație:</span>
              {post.canModerate ? (
                <select
                  className="text-xs bg-background border border-border rounded px-2 py-1 pr-8 min-w-[96px]"
                  value={moderationVisibility}
                  onChange={(e) => setModerationVisibility(e.target.value as Post['visibility'])}
                >
                  <option value="public">Publică</option>
                  <option value="school">Școală</option>
                  <option value="class">Clasă</option>
                </select>
              ) : (
                <span className="text-xs font-medium text-foreground">{visibilityLabel(post.visibility)}</span>
              )}
            </div>
          </div>
          {post.canModerate && (onApprove || onReject) ? (
            <div className="flex items-center gap-2">
              {onReject ? (
                <Button variant="outline" size="xs" onClick={() => onReject(post.id)}>
                  Respinge
                </Button>
              ) : null}
              {onApprove ? (
                <Button size="xs" onClick={() => onApprove(post.id, moderationVisibility)}>
                  Aprobă
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
      {post.isPinned && (
        <div className="flex items-center gap-2 px-4 py-2 bg-primary/5 border-b border-border">
          <Icon name="Pin" size={16} className="text-primary" />
          <span className="text-xs font-medium text-primary">Postare fixată</span>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0">
            {post.authorAvatar ? (
              <Image
                src={post.authorAvatar}
                alt={`Poză de profil: ${post.authorName}`}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground text-lg font-semibold">
                {post.authorName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className={`text-sm font-semibold ${getRoleColor(post.authorRole)} truncate`}>
                  {post.authorName}
                </h3>
                <p className="text-xs text-muted-foreground capitalize truncate">
                  {getRoleLabel(post.authorRole)} • {formatTimestamp(post.timestamp)}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0 max-w-[65%] sm:max-w-[50%]">
                <button
                  type="button"
                  className="px-2 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground transition-colors max-w-full truncate whitespace-nowrap"
                  onClick={() => onDestinationClick?.(post)}
                  title="Filtrează feed-ul după destinația postării"
                >
                  {destinationText()}
                </button>
                <span className={`px-2 py-1 rounded-md text-xs font-medium ${getCategoryColor(post.category)}`}>
                  {getCategoryLabel(post.category)}
                </span>
                {canDeletePost && onDeletePost ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    iconName="Trash2"
                    className="border-error/30 text-error hover:bg-error/10 hover:text-error"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onDeletePost(post.id);
                    }}
                    title="Șterge postarea"
                  >
                    Șterge
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {post.event ? (
          <div className="mb-3 rounded-md border border-border bg-muted/30 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground truncate">{post.event.title || 'Eveniment'}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {post.event.startAt ? post.event.startAt.toLocaleString('ro-RO') : '—'}
                  {post.event.location ? ` • ${post.event.location}` : ''}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Înscriși: {post.event.registrations}
                  {post.event.capacity != null ? ` / ${post.event.capacity}` : ''}
                </div>
              </div>
              {onToggleEventRegistration ? (
                <Button
                  variant={post.event.isRegistered ? 'outline' : 'default'}
                  size="sm"
                  onClick={() => onToggleEventRegistration(post.id, !post.event?.isRegistered)}
                >
                  {post.event.isRegistered ? 'Renunță' : 'Înscrie-te'}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="mb-3">
          <p className="text-sm text-foreground whitespace-pre-wrap">
            {isExpanded ? post.content : contentPreview}
          </p>
          {post.content.length > 300 && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-primary hover:underline mt-1"
            >
              {isExpanded ? 'Arată mai puțin' : 'Citește mai mult'}
            </button>
          )}
        </div>

        {post.images && post.images.length > 0 && (
          <div className={`grid gap-2 mb-3 ${
            post.images.length === 1 ? 'grid-cols-1' : 
            post.images.length === 2 ? 'grid-cols-2': 'grid-cols-2 md:grid-cols-3'
          }`}>
            {post.images.map((image, index) => (
              <button
                key={index}
                type="button"
                className="relative overflow-hidden rounded-md aspect-video focus:outline-none focus:ring-2 focus:ring-primary bg-muted/20"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openImageModal(index);
                }}
                aria-label="Vezi imaginea"
              >
                <Image
                  src={image.url}
                  alt={image.alt}
                  className={`w-full h-full ${isAwardImageUrl(image.url) ? 'object-contain' : 'object-cover hover:scale-105'} transition-transform duration-200`}
                />
              </button>
            ))}
          </div>
        )}

        {post.videos && post.videos.length > 0 && (
          <div className="space-y-2 mb-3">
            {post.videos.map((v, idx) => (
              <div key={idx} className="rounded-md overflow-hidden border border-border bg-muted/20">
                <AutoPlayVideo src={v.url} />
              </div>
            ))}
          </div>
        )}

        {post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {post.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md hover:bg-muted/80 transition-colors duration-150"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {!showCommentInput && comments.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="bg-muted/40 rounded-md p-3">
              <div className="text-xs font-medium text-muted-foreground mb-2">Comentarii</div>
              <div className="space-y-3">
                {buildCommentTree(comments)
                  .slice(0, 3)
                  .map((c) => renderComment(c, 0))}
              </div>
              {commentsHasMore || (post.comments || 0) > Math.min(comments.length, 3) ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowCommentInput(true);
                    onViewComments(post.id);
                  }}
                  className="text-xs text-primary hover:underline mt-2"
                >
                  Vezi toate comentariile
                </button>
              ) : null}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onThumbLike(post.id);
              }}
              className={`flex items-center gap-1.5 transition-colors duration-150 ${
                post.isLiked ? 'text-primary' : 'text-muted-foreground hover:text-primary'
              }`}
            >
              <Icon name="ThumbsUp" size={18} />
            </button>
            <button
              type="button"
              className="text-sm font-medium text-muted-foreground hover:text-primary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onViewPostReactions?.(post.id, 'thumb');
              }}
              disabled={!onViewPostReactions || (post.likes || 0) < 1}
              aria-label="Vezi aprecierile"
            >
              {post.likes}
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAdore(post.id);
              }}
              className={`flex items-center gap-1.5 transition-colors duration-150 ${
                post.isAdored ? 'text-error' : 'text-muted-foreground hover:text-error'
              }`}
            >
              <Icon name="Heart" size={18} />
            </button>
            <button
              type="button"
              className="text-sm font-medium text-muted-foreground hover:text-error"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onViewPostReactions?.(post.id, 'adore');
              }}
              disabled={!onViewPostReactions || (post.adores || 0) < 1}
              aria-label="Vezi adorurile"
            >
              {post.adores || 0}
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const next = !showCommentInput;
                setShowCommentInput(next);
                if (next) onLoadCommentsPreview?.(post.id);
              }}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors duration-150"
            >
              <Icon name="MessageCircle" size={18} />
              <span className="text-sm font-medium">{post.comments}</span>
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onShare(post.id);
              }}
              className="flex items-center gap-1.5 text-muted-foreground hover:text-accent transition-colors duration-150"
            >
              <Icon name="Share2" size={18} />
              <span className="text-sm font-medium">{post.shares}</span>
            </button>
          </div>

          {post.comments > 0 && (
            <button type="button"
              onClick={() => {
                setShowCommentInput(true);
                onViewComments(post.id);
              }}
              className="text-xs text-primary hover:underline"
            >
              Vezi toate comentariile
            </button>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type="text"
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Scrie un comentariu..."
              className="w-full sm:flex-1 px-3 py-2 text-sm bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment()}
            />
            <Button variant="default" size="sm" onClick={handleSubmitComment} disabled={!commentText.trim()} iconName="Send" className="w-full sm:w-auto">
              Publică
            </Button>
          </div>
        </div>

        {showCommentInput && (
          <div className="mt-3 pt-3 border-t border-border">
            {commentsLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                <Icon name="Loader2" size={14} className="animate-spin" />
                <span>Se încarcă comentariile…</span>
              </div>
            ) : null}

            {!commentsLoading && comments.length > 0 ? (
              <div className="bg-muted/40 rounded-md p-3 mb-3">
                <div className="text-xs font-medium text-muted-foreground mb-2">Comentarii</div>
                <div className="space-y-3">
                  {buildCommentTree(comments).map((c) => renderComment(c, 0))}
                </div>
                {commentsHasMore ? (
                  <button onClick={() => onViewComments(post.id)} className="text-xs text-primary hover:underline mt-2">
                    Vezi toate comentariile
                  </button>
                ) : null}
              </div>
            ) : null}

          </div>
        )}
      </div>
    </div>

    {imageModalOpen && (post.images || []).length > 0 ? (
      <>
        <div className="fixed inset-0 bg-black/70 z-[1200]" onClick={closeImageModal} />
        <div className="fixed inset-0 z-[1210] flex items-center justify-center p-4">
          <div
            className="relative w-full max-w-5xl"
            role="dialog"
            aria-modal="true"
            aria-label="Galerie imagini"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-2 right-2 flex items-center gap-2">
              <div className="text-xs text-white/80">
                {imageModalIndex + 1}/{(post.images || []).length}
              </div>
              <Button variant="ghost" size="sm" onClick={closeImageModal} iconName="X" className="text-white hover:bg-white/10" />
            </div>

            {(post.images || []).length > 1 ? (
              <button
                type="button"
                onClick={goPrevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white"
                aria-label="Imaginea anterioară"
              >
                <Icon name="ChevronLeft" size={24} />
              </button>
            ) : null}

            <div className="flex items-center justify-center">
              <Image
                src={(post.images || [])[imageModalIndex]?.url}
                alt={(post.images || [])[imageModalIndex]?.alt || 'Imagine'}
                className="max-h-[80vh] w-auto max-w-full object-contain rounded-md shadow-2xl"
              />
            </div>

            {(post.images || []).length > 1 ? (
              <button
                type="button"
                onClick={goNextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white"
                aria-label="Imaginea următoare"
              >
                <Icon name="ChevronRight" size={24} />
              </button>
            ) : null}
          </div>
        </div>
      </>
    ) : null}
    </>
  );
};

export default PostCard;
